// A17.02-22 — Infratuzilma kengaytmasi. MyGram bir-uzelli (single-node) bo'lgani
// uchun "ko'p-DC" ekranlar SOXTA emas, HAQIQIY yagona-uzel ma'lumoti + admin
// registrlari bilan to'ldiriladi. Barcha endpointlar REAL, deployed backend:
//   GET  /admin/infra/host          — jonli uzel metrikalari         A17.02/04
//   GET/POST/PUT/DELETE /admin/infra/servers[/:id] — server registri  A17.02
//   GET  /admin/infra/datacenters   — DC guruhlash + user biriktirish A17.03/05
//   GET  /admin/infra/slow-queries  — pg_stat_statements (mavjud bo'lsa) A17.11
//   GET  /admin/infra/api-metrics   — jarayon so'rov-hisoblagichlari  A17.12
//   GET/PUT /admin/infra/cdn        — CDN config (app_settings)       A17.17
//   GET/POST /admin/infra/backups   — backup + restore-drill registri A17.19/20
//   GET/POST /admin/infra/deploys + rollback — deploy tarixi          A17.21/22
import { api } from '@/lib/api'

const opt = (signal?: AbortSignal) => (signal ? { signal } : undefined)

/* ── A17.02/04 live host ── */
export interface HostInfo {
  hostname: string
  os: string
  arch: string
  cpu_cores: number
  load1: number
  load5: number
  load15: number
  load_per_core: number
  mem_total: number
  mem_available: number
  mem_used_pct: number
  disk_total: number
  disk_free: number
  disk_used_pct: number
  host_uptime_s: number
  proc_uptime_s: number
  goroutines: number
  heap_alloc: number
  go_version: string
  active_conns: number
  user_count: number
  db_pool_acquired: number
  db_pool_idle: number
  db_pool_total: number
  db_pool_max: number
}
export const fetchHost = (s?: AbortSignal) => api.get<{ host: HostInfo }>('/admin/infra/host', opt(s)).then((d) => d.host)

/* ── A17.02 servers registry ── */
export interface ServerRow {
  id: number
  name: string
  role: string
  region: string
  datacenter: string
  hostname: string
  ip_addr: string
  status: string
  notes: string
  created_by: string
  created_at: string
  updated_at: string
}
export interface ServerInput {
  name: string
  role: string
  region: string
  datacenter: string
  hostname: string
  ip_addr: string
  status: string
  notes: string
}
export const fetchServersRegistry = (s?: AbortSignal) =>
  api.get<{ servers: ServerRow[] }>('/admin/infra/servers', opt(s)).then((d) => d.servers ?? [])
export const createServer = (b: ServerInput) => api.post<{ id: number }>('/admin/infra/servers', b)
export const updateServer = (id: number, b: ServerInput) => api.put(`/admin/infra/servers/${id}`, b)
export const deleteServer = (id: number) => api.del(`/admin/infra/servers/${id}`)

/* ── A17.03/05 data centers ── */
export interface DcRow {
  name: string
  servers: number
  active_servers: number
  regions: string
  is_primary: boolean
  live_node?: {
    hostname: string
    os: string
    cpu_cores: number
    load1: number
    mem_used_pct: number
    disk_used_pct: number
  }
}
export interface DataCentersResp {
  datacenters: DcRow[]
  primary: string
  user_count: number
  total_dcs: number
  single_node: boolean
}
export const fetchDataCenters = (s?: AbortSignal) => api.get<DataCentersResp>('/admin/infra/datacenters', opt(s))

/* ── A17.11 slow queries ── */
export interface SlowQuery {
  query_id: string
  query: string
  calls: number
  total_ms: number
  mean_ms: number
  rows: number
}
export interface SlowQueriesResp {
  available: boolean
  queries: SlowQuery[]
}
export const fetchSlowQueries = (s?: AbortSignal) =>
  api.get<SlowQueriesResp>('/admin/infra/slow-queries?limit=50', opt(s))

/* ── A17.12 API metrics ── */
export interface ApiMetrics {
  req_2xx: number
  req_3xx: number
  req_4xx: number
  req_5xx: number
  total_requests: number
  in_flight: number
  duration_sum_ms: number
  duration_count: number
  avg_latency_ms: number
  error_rate_pct: number
  ws_active: number
  push_sent: number
  version: string
}
export const fetchApiMetrics = (s?: AbortSignal) =>
  api.get<{ metrics: ApiMetrics }>('/admin/infra/api-metrics', opt(s)).then((d) => d.metrics)

/* ── A17.17 CDN config ── */
export interface CdnConfig {
  enabled: boolean
  provider: string
  base_url: string
  zone: string
  notes: string
}
export const fetchCdn = (s?: AbortSignal) => api.get<{ cdn: CdnConfig }>('/admin/infra/cdn', opt(s)).then((d) => d.cdn)
export const saveCdn = (b: CdnConfig) => api.put('/admin/infra/cdn', b)

/* ── A17.19/20 backups ── */
export interface BackupRow {
  id: number
  kind: string
  status: string
  location: string
  size_bytes: number
  notes: string
  started_at: string
  finished_at: string | null
  created_by: string
}
export interface BackupInput {
  kind: string
  status: string
  location: string
  size_bytes: number
  notes: string
}
export const fetchBackups = (kind: string, s?: AbortSignal) =>
  api
    .get<{ backups: BackupRow[] }>(`/admin/infra/backups${kind ? `?kind=${kind}` : ''}`, opt(s))
    .then((d) => d.backups ?? [])
export const createBackup = (b: BackupInput) => api.post<{ id: number }>('/admin/infra/backups', b)

/* ── A17.21/22 deploys ── */
export interface DeployRow {
  id: number
  version: string
  git_sha: string
  environment: string
  status: string
  kind: string
  rollback_to: string
  notes: string
  deployed_by: string
  deployed_at: string
}
export interface DeploysResp {
  deploys: DeployRow[]
  schema_version: number
  build_version: string
}
export interface DeployInput {
  version: string
  git_sha: string
  environment: string
  status: string
  notes: string
}
export const fetchDeploys = (s?: AbortSignal) => api.get<DeploysResp>('/admin/infra/deploys', opt(s))
export const createDeploy = (b: DeployInput) => api.post<{ id: number }>('/admin/infra/deploys', b)
export const rollbackDeploy = (id: number, notes: string) =>
  api.post<{ id: number }>(`/admin/infra/deploys/${id}/rollback`, { notes })

/* ── strings (RU, panel tili) ── */
export const IX = {
  refresh: 'Обновить',
  loadFailed: 'Ошибка загрузки',
  add: 'Добавить',
  save: 'Сохранить',
  edit: 'Изменить',
  delete: 'Удалить',
  cancel: 'Отмена',
  done: 'Готово',
  failed: 'Не удалось',
  none: 'Нет',
  // host / servers
  liveNode: 'Живой узел (этот сервер)',
  hostname: 'Хост',
  os: 'ОС',
  cpu: 'CPU ядра',
  load: 'Нагрузка (1/5/15м)',
  loadPerCore: 'Нагрузка на ядро',
  memory: 'Память',
  disk: 'Диск',
  uptime: 'Аптайм',
  procUptime: 'Аптайм процесса',
  goroutines: 'Горутины',
  activeConns: 'Активные соединения БД',
  dbPool: 'Пул БД (занято/всего/макс)',
  servers: 'Реестр серверов',
  serversDesc: 'Живой узел + дополнительные серверы (админ-реестр). MyGram — одноузловой деплой',
  serverName: 'Название',
  role: 'Роль',
  region: 'Регион',
  datacenter: 'ДЦ',
  ipAddr: 'IP-адрес',
  status: 'Статус',
  notes: 'Заметки',
  noServers: 'Дополнительные серверы не добавлены',
  addServer: 'Добавить сервер',
  editServer: 'Изменить сервер',
  // data centers
  dc: 'Дата-центры (ДЦ)',
  dcDesc: 'Серверы, сгруппированные по ДЦ + живой узел. MyGram — один ДЦ',
  serversCount: 'Серверов',
  activeCount: 'Активных',
  regions: 'Регионы',
  primary: 'Основной',
  singleNodeNote: 'Одноузловой деплой — один логический ДЦ',
  // dc load
  dcLoad: 'Нагрузка ДЦ и распределение',
  dcLoadDesc: 'Реальные метрики единственного узла (CPU / память / диск / нагрузка)',
  cpuLoad: 'Загрузка CPU',
  memUsed: 'Память использовано',
  diskUsed: 'Диск использовано',
  // dc assignment
  dcAssign: 'Привязка пользователей к ДЦ',
  dcAssignDesc: 'Гео-шардинг: все пользователи на едином ДЦ (одноузловой деплой)',
  allUsers: 'Все пользователи',
  assignedTo: 'Привязаны к ДЦ',
  ofUsers: 'пользователей на основном ДЦ',
  // migration control
  migControl: 'Управление миграциями',
  migControlDesc: 'Текущая версия схемы БД + применённые/ожидающие миграции (schema_migrations)',
  currentVersion: 'Текущая версия схемы',
  applied: 'Применено',
  pending: 'Ожидает',
  migration: 'Миграция',
  version: 'Версия',
  // queues
  queues: 'Очереди (обработка задач)',
  queuesDesc: 'Живая глубина очередей Redis + пропускная способность. Стек MyGram на Go (не Horizon)',
  queueName: 'Очередь',
  depth: 'Глубина',
  mediaQueue: 'Обработка медиа',
  redisOps: 'Redis опер/сек',
  hitRate: 'Redis hit-rate',
  empty: 'Пусто',
  // slow queries
  slowQ: 'Медленные запросы',
  slowQDesc: 'Топ запросов по среднему времени (pg_stat_statements)',
  slowQUnavailable: 'Расширение pg_stat_statements не установлено',
  slowQUnavailableDesc:
    'Медленные запросы недоступны: включите pg_stat_statements в shared_preload_libraries на сервере PostgreSQL. Данные не выдумываются.',
  query: 'Запрос',
  calls: 'Вызовы',
  meanMs: 'Сред. мс',
  totalMs: 'Всего мс',
  rowsCol: 'Строки',
  // api latency
  apiLatency: 'Время ответа API',
  apiLatencyDesc: 'Живые счётчики процесса: запросы, задержка, ошибки, WS (/metrics)',
  avgLatency: 'Сред. задержка',
  totalReq: 'Всего запросов',
  inFlight: 'В обработке',
  errorRate: 'Доля ошибок',
  wsActive: 'Активные WS',
  pushSent: 'Push отправлено',
  byClass: 'По классам статуса',
  // cdn
  cdn: 'Состояние CDN',
  cdnDesc: 'Конфигурация внешнего CDN (админ-управляемая). Сохраняется в app_settings',
  cdnEnabled: 'CDN включён',
  cdnProvider: 'Провайдер',
  cdnBaseUrl: 'Базовый URL',
  cdnZone: 'Зона',
  cdnSaved: 'Настройки CDN сохранены',
  // backups
  backups: 'Состояние бэкапов',
  backupsDesc: 'Журнал резервных копий (server-cron → B2). Реестр запусков',
  restoreDrill: 'Тест восстановления (restore drill)',
  restoreDrillDesc: 'Журнал учений по восстановлению из бэкапа',
  kind: 'Тип',
  location: 'Расположение',
  size: 'Размер',
  started: 'Начато',
  finished: 'Завершено',
  recordBackup: 'Записать запуск',
  recordDrill: 'Записать учение',
  noBackups: 'Запусков ещё нет',
  // deploys
  deploys: 'История деплоев',
  deploysDesc: 'Журнал релизов + текущая версия схемы БД',
  buildVersion: 'Версия сборки',
  schemaVersion: 'Версия схемы',
  deployedBy: 'Кем',
  deployedAt: 'Когда',
  recordDeploy: 'Записать деплой',
  noDeploys: 'Деплоев ещё нет',
  gitSha: 'Git SHA',
  environment: 'Окружение',
  // rollback
  rollback: 'Откат релиза (rollback)',
  rollbackDesc: 'Выберите релиз для отката. Действие записывается в журнал + аудит',
  rollbackTo: 'Откатить на',
  rollbackConfirm: 'Откатить на эту версию?',
  rollbackNote: 'Причина отката',
  rollbackDone: 'Откат записан',
  current: 'Текущий',
} as const
